import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';

import {
  useGetBarBillingReportQuery,
  useLazyGetBarBillingReportExcelQuery,
} from '../../redux/api/barDeliveryApi';
import { useApiErrorToast } from '../../hooks/useApiError';
import { downloadBlob } from '../../utils/download';

const gebinde = (value) =>
  Number(value).toLocaleString('de-DE', { maximumFractionDigits: 2 });

const euro = (value) =>
  `${Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/**
 * The lines an invoice closes with. USt is charged on goods and deposit alike,
 * so it is one figure per rate over the combined net — not a goods-only figure
 * with the Pfand tacked on underneath.
 *
 * The per-rate split only appears when more than one rate is actually in play;
 * with everything at 19% the invoice wants a single 'USt (19%)' line.
 */
const PartnerSummary = ({ partner }) => {
  const { totals, vat_breakdown: rates, item_balances: items } = partner;

  return (
    <div className='row mt-3'>
      <div className='col-12 col-md-6'>
        <table className='table table-sm mb-0'>
          <tbody>
            <tr>
              <td>Summe Ware netto</td>
              <td className='text-end'>{euro(totals.goods_net)}</td>
            </tr>
            <tr>
              <td>Summe Pfand netto</td>
              <td className='text-end'>{euro(totals.deposit_net)}</td>
            </tr>
            <tr>
              <td>Summe netto</td>
              <td className='text-end'>{euro(totals.net)}</td>
            </tr>
            {rates.length === 1 ? (
              <tr>
                <td>USt ({rates[0].rate} %)</td>
                <td className='text-end'>{euro(totals.vat)}</td>
              </tr>
            ) : (
              rates.map((entry) => (
                <tr key={entry.rate}>
                  <td>
                    USt ({entry.rate} %) auf {euro(entry.net)} netto
                  </td>
                  <td className='text-end'>{euro(entry.vat)}</td>
                </tr>
              ))
            )}
            <tr className='table-secondary'>
              <th>Gesamtsumme brutto</th>
              <th className='text-end'>{euro(totals.total_gross)}</th>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Flaschen/Becher are counted per label, never added together — a
          number mixing cups and bottles is one nobody can act on. */}
      {items.length > 0 && (
        <div className='col-12 col-md-6 mt-3 mt-md-0'>
          {items.map((entry) => (
            <table className='table table-sm mb-3' key={entry.item_label}>
              <tbody>
                <tr>
                  <td>{entry.item_label} AUSGEGEBEN</td>
                  <td className='text-end'>{entry.handed_out}</td>
                </tr>
                <tr>
                  <td>{entry.item_label} ZURÜCKGEGEBEN</td>
                  <td className='text-end'>{entry.returned}</td>
                </tr>
                <tr className='table-secondary'>
                  <th>{entry.item_label} über/unter</th>
                  <th className='text-end'>{entry.balance}</th>
                </tr>
              </tbody>
            </table>
          ))}
        </div>
      )}
    </div>
  );
};

const BarBillingReport = () => {
  const eventId = useSelector((state) => state.auth.currentEvent?.id);
  const { data, isLoading } = useGetBarBillingReportQuery(
    { eventId },
    { skip: !eventId }
  );
  const [expanded, setExpanded] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [fetchExcel] = useLazyGetBarBillingReportExcelQuery();
  const apiErrorToast = useApiErrorToast();

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const blob = await fetchExcel({ eventId }).unwrap();
      downloadBlob(blob, `bar-abrechnung-${data?.event_name || eventId}.xlsx`);
    } catch (err) {
      apiErrorToast(err, 'Excel-Export konnte nicht erstellt werden');
    } finally {
      setDownloading(false);
    }
  };

  if (!eventId) {
    return (
      <div className='alert alert-warning'>Bitte zuerst ein Event wählen.</div>
    );
  }
  if (isLoading) return <p className='text-muted'>Lade Abrechnung...</p>;
  if (!data) {
    return (
      <div className='alert alert-danger'>Fehler beim Laden der Abrechnung.</div>
    );
  }

  return (
    <>
      <div className='d-flex justify-content-between align-items-center'>
        <h4>Bar-Abrechnung — {data.event_name}</h4>
        <button
          className='btn btn-success'
          onClick={downloadExcel}
          disabled={downloading}
        >
          <FontAwesomeIcon icon={faFileExcel} className='me-2' />
          Excel-Export
        </button>
      </div>

      <div className='alert alert-info mt-3'>
        Abgerechnet wird die tatsächlich gelieferte Menge abzüglich zurück­­
        genommener Ware. Pfand wird je <b>Pfandgruppe</b> abgerechnet: berechnet
        wird die gelieferte Menge, gutgeschrieben jede{' '}
        <b>Rücknahmeart</b> zu ihrem eigenen Satz. Die Differenz ist Pfand, das
        die Bar nicht zurückerhält. <b>Artikelpreise sind brutto</b>,{' '}
        <b>Pfandsätze netto</b> — die USt wird auf Ware und Pfand gemeinsam
        ausgewiesen. Preise werden zum Abrufzeitpunkt aus den Artikelstammdaten
        gelesen — Artikelpreise während des Events bitte nicht mehr ändern.
      </div>

      {data.partners.length === 0 ? (
        <div className='alert alert-warning'>
          Keine bestätigten Bar-Lieferungen für dieses Event.
        </div>
      ) : (
        <>
          <table className='table table-striped align-middle mt-4'>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Bars</th>
                <th className='text-end'>Ware netto</th>
                <th className='text-end'>Pfand netto</th>
                <th className='text-end'>Summe netto</th>
                <th className='text-end'>USt</th>
                <th className='text-end'>Gesamt brutto</th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {data.partners.map((partner) => (
                <React.Fragment key={partner.partner_id}>
                  <tr>
                    <td>
                      <b>{partner.partner_name}</b>
                      <div className='text-muted small'>{partner.contact}</div>
                    </td>
                    <td className='small'>
                      {partner.locations.map((l) => l.name).join(', ')}
                    </td>
                    <td className='text-end'>
                      {euro(partner.totals.goods_net)}
                    </td>
                    <td className='text-end'>
                      {euro(partner.totals.deposit_net)}
                    </td>
                    <td className='text-end'>{euro(partner.totals.net)}</td>
                    <td className='text-end'>{euro(partner.totals.vat)}</td>
                    <td className='text-end'>
                      <b>{euro(partner.totals.total_gross)}</b>
                    </td>
                    <td>
                      <button
                        className='btn btn-outline-secondary btn-sm'
                        onClick={() =>
                          setExpanded(
                            expanded === partner.partner_id
                              ? null
                              : partner.partner_id
                          )
                        }
                      >
                        {expanded === partner.partner_id
                          ? 'zuklappen'
                          : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {expanded === partner.partner_id && (
                    <tr>
                      <td colSpan='8' className='bg-body-tertiary'>
                        <div className='table-responsive'>
                          <table className='table table-sm mb-0'>
                            <thead>
                              <tr>
                                <th>Artikel</th>
                                <th className='text-end'>geliefert</th>
                                <th className='text-end'>Ware retour</th>
                                <th className='text-end'>abgerechnet</th>
                                <th className='text-end'>Preis</th>
                                <th className='text-end'>Brutto</th>
                              </tr>
                            </thead>
                            <tbody>
                              {partner.articles.map((row) => (
                                <tr key={row.article_id}>
                                  <td>
                                    {row.article_name}
                                    {row.unit_name && (
                                      <span className='text-muted ms-1'>
                                        ({row.unit_name})
                                      </span>
                                    )}
                                  </td>
                                  <td className='text-end'>{row.delivered}</td>
                                  <td className='text-end'>
                                    {row.goods_returned}
                                  </td>
                                  <td className='text-end'>
                                    <b>{row.billed_quantity}</b>
                                  </td>
                                  <td className='text-end'>
                                    {euro(row.price)}
                                  </td>
                                  <td className='text-end'>
                                    {euro(row.gross)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {partner.deposit_groups.length > 0 && (
                          <div className='table-responsive mt-3'>
                            <b>Pfand je Pfandgruppe</b>
                            <table className='table table-sm mb-0'>
                              <thead>
                                <tr>
                                  <th>Pfandgruppe / Rücknahmeart</th>
                                  <th className='text-end'>geliefert</th>
                                  <th className='text-end'>Ware retour</th>
                                  <th className='text-end'>Menge zurück</th>
                                  <th className='text-end'>= Gebinde</th>
                                  <th className='text-end'>offen</th>
                                  <th className='text-end'>berechnet</th>
                                  <th className='text-end'>gutgeschrieben</th>
                                  <th className='text-end'>Pfand netto</th>
                                  <th className='text-end'>ausgegeben</th>
                                  <th className='text-end'>zurück</th>
                                </tr>
                              </thead>
                              <tbody>
                                {partner.deposit_groups.map((row) => (
                                  <React.Fragment key={row.deposit_group_id}>
                                    <tr>
                                      <td>
                                        <b>{row.deposit_group_name}</b>
                                        {row.over_returned && (
                                          <span
                                            className='badge bg-warning text-dark ms-2'
                                            title='Mehr Leergut zurück als geliefert — bei gemischten Paletten normal, bitte prüfen.'
                                          >
                                            Überhang
                                          </span>
                                        )}
                                      </td>
                                      <td className='text-end'>
                                        {row.delivered}
                                      </td>
                                      <td className='text-end'>
                                        {row.goods_returned}
                                      </td>
                                      <td className='text-end'>&nbsp;</td>
                                      <td className='text-end'>
                                        {gebinde(row.returned_gebinde)}
                                      </td>
                                      <td className='text-end'>
                                        <b>{gebinde(row.outstanding)}</b>
                                      </td>
                                      <td className='text-end'>
                                        {euro(row.charged)}
                                      </td>
                                      <td className='text-end'>
                                        {euro(row.credited)}
                                      </td>
                                      <td className='text-end'>
                                        {euro(row.deposit_net)}
                                      </td>
                                      <td className='text-end'>
                                        {row.counts_items
                                          ? row.items_handed_out
                                          : '—'}
                                      </td>
                                      <td className='text-end'>
                                        {row.counts_items
                                          ? row.items_returned
                                          : '—'}
                                      </td>
                                    </tr>
                                    {/* Each way the Gebinde came back is its
                                        own named position — 'davon Leerkasten'
                                        says nothing about a box of cups. */}
                                    {row.returns.map((kind) => (
                                      <tr
                                        key={kind.deposit_kind_id}
                                        className='small text-muted'
                                      >
                                        <td className='ps-4'>
                                          {kind.deposit_kind_name}
                                          {kind.units_per_return > 1 && (
                                            <span className='ms-2'>
                                              ({kind.units_per_return} = 1
                                              Gebinde)
                                            </span>
                                          )}
                                        </td>
                                        <td className='text-end'>&nbsp;</td>
                                        <td className='text-end'>&nbsp;</td>
                                        <td className='text-end'>
                                          {kind.quantity}
                                        </td>
                                        <td className='text-end'>
                                          {gebinde(kind.gebinde_equivalent)}
                                        </td>
                                        <td className='text-end'>&nbsp;</td>
                                        <td className='text-end'>
                                          {euro(kind.deposit_per_unit)}
                                        </td>
                                        <td className='text-end'>
                                          {euro(kind.credited)}
                                        </td>
                                        <td className='text-end'>&nbsp;</td>
                                        <td className='text-end'>&nbsp;</td>
                                        <td className='text-end'>
                                          {row.counts_items
                                            ? kind.items_returned
                                            : ''}
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <PartnerSummary partner={partner} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className='table-secondary'>
                <th colSpan='2'>Summe</th>
                <th className='text-end'>{euro(data.totals.goods_net)}</th>
                <th className='text-end'>{euro(data.totals.deposit_net)}</th>
                <th className='text-end'>{euro(data.totals.net)}</th>
                <th className='text-end'>{euro(data.totals.vat)}</th>
                <th className='text-end'>{euro(data.totals.total_gross)}</th>
                <th>&nbsp;</th>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </>
  );
};

export default BarBillingReport;
